<template>
    <base-dialog :loading="loading" :value="dialog" :width="560" :title="$t('company.pending_coach_invitations')"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="invitations-filters">
            <kimsa-text-field @change="search = $event" :value="search" :placeholder="$t('common.search')"
                prepend-inner-icon="mdi-magnify" dense />
        </div>

        <skeleton-loader v-if="loading" type="rows" :count="3" />
        <template v-else>
            <div v-if="filtered.length === 0" class="invitations-empty">
                {{ $t('company.no_pending_coach_invitations') }}
            </div>
            <div v-for="item in paged" :key="item.id" class="invitation-row">
                <div class="invitation-info">
                    <span class="invitation-email">{{ item.email }}</span>
                    <span class="invitation-date">{{ sentLabel(item) }}</span>
                </div>
                <div class="invitation-actions">
                    <button-tooltip top :tooltip="$t('tooltips.resend_invitation')">
                        <template v-slot:activator="{ on, attrs }">
                            <button class="invitation-btn" v-bind="attrs" v-on="on" :disabled="!!actionId"
                                @click="resend(item)">
                                <v-progress-circular v-if="actionId === item.id" indeterminate size="14" width="2" />
                                <v-icon v-else size="14">mdi-email-sync-outline</v-icon>
                            </button>
                        </template>
                    </button-tooltip>
                    <button-tooltip top :tooltip="$t('tooltips.cancel_invitation')">
                        <template v-slot:activator="{ on, attrs }">
                            <button class="invitation-btn del" v-bind="attrs" v-on="on" :disabled="!!actionId"
                                @click="showCancelConfirm(item)">
                                <v-icon size="14">mdi-trash-can-outline</v-icon>
                            </button>
                        </template>
                    </button-tooltip>
                </div>
            </div>

            <div v-if="filtered.length > 0" class="invitations-footer">
                <span class="invitations-count">
                    <span class="invitations-count-num">{{ filtered.length }}</span>
                    {{ $t('coach.invitations_count_suffix', { total: invitations.length }) }}
                </span>
                <kimsa-pagination v-if="pageCount > 1" class="ml-auto" height="auto" @page="page = $event"
                    :value="page" :pageCount="pageCount" :total-visible="isMobile ? 4 : 7" />
            </div>
        </template>

        <kimsa-confirm ref="cancel" :title="$t('coach.cancel_invitation_title')"
            :content="$t('company.cancel_coach_invitation_subtitle', { email: current && current.email || '' })"
            :loading="loadingCancel" :persistent="false" @confirm="onConfirmCancel" @cancel="current = null"
            :close-on-confirm="false" :alert-mode="true" />

        <template v-slot:footer>
            <v-btn block depressed x-large color="primary" class="footer-btn black--text" @click="hide">
                {{ $t('common.close') }}
            </v-btn>
        </template>
    </base-dialog>
</template>

<script>
import moment from "moment";
import firebase, { db } from "@/utils/Firebase";
import BaseDialog from "@/components/common/BaseDialog";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import KimsaPagination from "@/components/common/KimsaPagination";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import SkeletonLoader from "@/components/common/SkeletonLoader";

const PER_PAGE = 8

export default {
    name: "CoachInvitationsDialog",
    components: { BaseDialog, ButtonTooltip, KimsaConfirm, KimsaPagination, KimsaTextField, SkeletonLoader },
    data() {
        return {
            dialog: false,
            loading: false,
            loadingCancel: false,
            actionId: null,
            invitations: [],
            current: null,
            search: '',
            page: 1,
        }
    },
    computed: {
        filtered(vm = this) {
            const search = vm.search.trim().toLowerCase()
            if (!search) return vm.invitations
            return vm.invitations.filter(invitation => (invitation.email || '').includes(search))
        },
        paged(vm = this) {
            return vm.filtered.slice((vm.page - 1) * PER_PAGE, vm.page * PER_PAGE)
        },
        pageCount(vm = this) {
            return Math.ceil(vm.filtered.length / PER_PAGE)
        },
    },
    methods: {
        inviteCoach: firebase.functions().httpsCallable('httpInvitations-inviteCoach'),
        cancelInvitation: firebase.functions().httpsCallable('httpInvitations-cancelCoachInvitation'),
        show() {
            const vm = this
            vm.dialog = true
            vm.loadInvitations()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.invitations = []
            vm.current = null
            vm.actionId = null
            vm.search = ''
            vm.page = 1
        },
        async loadInvitations() {
            const vm = this
            vm.loading = true
            vm.page = 1
            try {
                const invitationsRef = await db.collection('coachInvitations')
                    .where('company.id', '==', vm.currentUser.uid)
                    .where('status', '==', 'pending')
                    .orderBy('createdAt', 'desc')
                    .limit(300)
                    .get()

                vm.invitations = invitationsRef.docs.map(doc => ({ id: doc.id, ...doc.data() }))
            } catch (e) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loading = false
            }
        },
        sentAt(invitation) {
            const date = invitation.resentAt || invitation.createdAt
            return date?.seconds ? date.seconds * 1000 : 0
        },
        sentLabel(invitation) {
            const vm = this
            const time = vm.sentAt(invitation)
            if (!time) return ''
            const key = invitation.resentAt ? 'coach.invitation_resent_at' : 'coach.invitation_sent_at'
            return vm.$t(key, { date: moment(time).format('DD/MM/YYYY') })
        },
        async resend(invitation) {
            const vm = this
            vm.actionId = invitation.id
            try {
                await vm.inviteCoach({ email: invitation.email })
                vm.toast(vm.$t('coach.invitation_resent'))
                await vm.loadInvitations()
            } catch (err) {
                vm.toast(err.message || vm.$t('coach.student_invitation_failed'), 'error')
            }
            vm.actionId = null
        },
        showCancelConfirm(invitation) {
            const vm = this
            vm.current = invitation
            vm.$refs.cancel.show()
        },
        async onConfirmCancel() {
            const vm = this
            const invitation = vm.current
            vm.loadingCancel = true
            try {
                await vm.cancelInvitation({ invitationID: invitation.id })
                vm.invitations = vm.invitations.filter(item => item.id !== invitation.id)
                if (vm.page > 1 && vm.page > vm.pageCount) vm.page = vm.pageCount
                vm.current = null
                vm.$refs.cancel?.hide()
                vm.toast(vm.$t('coach.invitation_canceled'))
            } catch (err) {
                vm.toast(err.message || vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loadingCancel = false
            }
        },
    },
    watch: {
        search() {
            this.page = 1
        },
    },
}
</script>

<style scoped>
.invitations-filters {
    padding-bottom: 14px;
}

.invitations-empty {
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    padding: 24px 0;
}

.invitation-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 8px;
    border-top: 1px solid var(--v-outline-base);
}

.invitation-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
}

.invitation-email {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.invitation-date {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.invitation-actions {
    display: flex;
    gap: 4px;
}

.invitation-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline2-base);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtMuted-base);
    transition: all 0.12s;
}

.invitation-btn .v-icon {
    color: inherit;
}

.invitation-btn:disabled {
    opacity: 0.5;
    cursor: default;
}

.invitation-btn:not(:disabled):hover {
    color: var(--v-txtPrimary-base);
    background: var(--v-outline2-base);
}

.invitation-btn.del:not(:disabled):hover {
    color: var(--v-error-base);
    border-color: var(--v-error-base);
    background: rgba(232, 92, 92, 0.08);
}

.invitations-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--v-outline-base);
}

.invitations-count {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.invitations-count-num {
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.footer-btn {
    border-radius: 0 !important;
    height: 52px !important;
    font-weight: 700;
}
</style>
